/**
 * Cómo se lee la hora de un aviso de la campana.
 *
 * ── EL FALLO QUE ARREGLA ────────────────────────────────────────────────────
 *
 * Lissette, 2026-09-01: *"la notificación de cuando alguien envió o firmó o lo
 * que sea, en la campana muestra hora UTC, no está agarrando la hora
 * configurada por el agente"*.
 *
 * El contenedor del servidor corre en UTC, y el aviso de firma escribía la
 * hora YA FORMATEADA dentro del texto:
 *
 *     body: `Firmado: ${signedAt.toISOString().slice(0,16).replace('T',' ')} UTC`
 *
 * Una firma de las 10:35 de la noche en Florida salía como "02:35 UTC" del día
 * siguiente. No es que la hora estuviera mal: es que era la de otro sitio, y
 * encima con la fecha corrida.
 *
 * ── POR QUÉ SE RESUELVE EN EL NAVEGADOR Y NO EN EL SERVIDOR ─────────────────
 *
 * La agencia tiene una zona horaria configurada (`Agency.timezone`), y es la
 * correcta para decidir CUÁNDO PASA algo: "todos los lunes a las 8" tiene que
 * significar las 8 de la agencia. Pero esto es otra cosa — es una hora que
 * alguien está MIRANDO ahora mismo. Para eso lo que manda es el reloj de quien
 * mira, que el navegador ya sabe sin que nadie lo configure, y que además
 * acierta con la agente que está de viaje.
 *
 * Regla que deja: una hora que se GUARDA viaja en UTC; una hora que se LEE se
 * formatea donde se lee.
 *
 * Vive fuera del componente para poder ejecutarlo en las pruebas sin montar
 * React, y `ahora` entra por parámetro para que no dependan de la hora real.
 */

/** Hace cuánto, en corto: "ahora", "5m", "3h", "2d". */
export function haceCuanto(iso, lang, ahora = new Date()) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const minutos = Math.floor((ahora - d) / 60000);
  // Un aviso recién creado puede llegar con un reloj un pelo adelantado
  // respecto al del navegador. "en -1m" no se le enseña a nadie.
  if (minutos < 1) return lang === 'es' ? 'ahora' : 'now';
  if (minutos < 60) return `${minutos}m`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `${horas}h`;
  const dias = Math.floor(horas / 24);
  if (dias < 7) return `${dias}d`;
  // Más de una semana: la fecha. `toLocaleDateString` sin zona usa la del
  // navegador, que es justo la que se quiere.
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * La hora exacta, en el reloj de quien mira. Va en el tooltip: "3h" dice
 * cuánto hace, y esto dice a qué hora fue, sin tener que abrir nada.
 *
 * Sin `timeZone`, `toLocaleString` usa la del navegador — que es el arreglo
 * entero. Y siempre en-US, como todas las fechas del CRM.
 */
export function horaExacta(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * LOS AVISOS QUE YA ESTABAN GUARDADOS SIGUEN DICIENDO "UTC".
 *
 * Lissette, 2026-09-01, con el arreglo ya desplegado: *"aquí sigue diciendo
 * UTC"* — y en su captura el aviso era **de hace cinco días**.
 *
 * Y tenía razón las dos veces. El arreglo del servidor quitó la hora del texto
 * de los avisos NUEVOS, pero los que ya existían la llevan ESCRITA DENTRO del
 * cuerpo, en la base de datos:
 *
 *     "Firmado: 2026-08-26 15:34 UTC"
 *
 * Eso no lo cambia un despliegue: es un dato guardado. Se arregla al PINTARLO.
 *
 * ── POR QUÉ AQUÍ Y NO CON UNA MIGRACIÓN ────────────────────────────────────
 *
 * Una migración tendría que decidir a qué hora local convertir cada fila, y esa
 * decisión no le corresponde: no se sabe quién va a leer el aviso ni desde
 * dónde. Convertir al pintar acierta siempre —incluida la agente que está de
 * viaje—, no toca ningún dato, y funciona en el acto para los avisos viejos sin
 * esperar a nada.
 *
 * Lo guardado se queda como está, que además es lo correcto: en la base de
 * datos la hora en UTC es el registro honesto de cuándo pasó.
 */

// "2026-08-26 15:34 UTC" o con segundos. Se exige el sello COMPLETO —fecha,
// hora y la palabra UTC— para no tocar por accidente un texto que solo lleve
// una fecha suelta (un número de póliza, una fecha de vigencia).
const SELLO_UTC = /(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?\s*UTC\b/g;

/**
 * El cuerpo del aviso con cualquier hora en UTC ya pasada al reloj de quien
 * mira. Un cuerpo sin sello vuelve TAL CUAL, sin tocarle una letra.
 */
export function cuerpoDelAviso(body) {
  const s = String(body || '');
  if (!s) return '';
  return s.replace(SELLO_UTC, (entero, a, m, d, hh, mm, ss) => {
    const fecha = new Date(Date.UTC(+a, +m - 1, +d, +hh, +mm, +(ss || 0)));
    // UNA FECHA IMPOSIBLE SE DEJA COMO ESTÁ, y hay que comprobarlo A MANO.
    // `Date.UTC` NO devuelve error con un "2026-13-45 99:99": lo DESBORDA hasta
    // "17 feb 2027", una fecha creíble y falsa — peor que no convertir nada,
    // porque nadie sospecharía de ella. Así que se exige que la fecha construida
    // diga exactamente lo mismo que el texto de donde salió.
    if (Number.isNaN(fecha.getTime())) return entero;
    const mismos = fecha.getUTCFullYear() === +a
      && fecha.getUTCMonth() === +m - 1
      && fecha.getUTCDate() === +d
      && fecha.getUTCHours() === +hh
      && fecha.getUTCMinutes() === +mm;
    if (!mismos) return entero;
    return horaExacta(fecha.toISOString());
  });
}
